import { useState, useRef, useCallback, useEffect } from 'react';

import { TextField, Box, Button, Typography } from '@mui/material';
import { getStoredCsrfToken, redirectLogin, CSRF_TOKEN_HEADER } from '@ragu-components/ord-providers';
import axios from 'axios';
import { useTranslate, useAuthProvider, useNotify } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import type { MatchReplaceTestValue } from '../../flowchart/dataProvider/types';
import type { MatchReplaceTestResponse, OrdJsonErrorResponse } from '@ragu-components/ord-types';
import type { AxiosResponse } from 'axios';

interface MatchReplaceTestInputProps {
  label: string;
  matchRules: MatchReplaceTestValue[];
  onTestResult: (matchedIndex: number | null) => void;
}

const getValidationMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError<OrdJsonErrorResponse>(error)) {
    return null;
  }
  const responseData = error.response?.data;
  if (responseData === undefined) {
    return null;
  }
  const firstError = responseData.errors?.[0];
  if (firstError === undefined) {
    return null;
  }
  return firstError.detail !== '' ? firstError.detail : firstError.title;
};

const MatchReplaceTestInput = ({ matchRules, label, onTestResult }: MatchReplaceTestInputProps): React.ReactElement => {
  const [testInput, setTestInput] = useState<string>('');
  const [replacementMessage, setReplacementMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const replacementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const translate = useTranslate();
  const authProvider = useAuthProvider();
  const notify = useNotify();
  const axiosInstance = axios.create();
  axiosInstance.defaults.headers.common[CSRF_TOKEN_HEADER] = getStoredCsrfToken() ?? '';

  const clearReplacementTimeout = useCallback((): void => {
    if (replacementTimeoutRef.current !== null) {
      clearTimeout(replacementTimeoutRef.current);
      replacementTimeoutRef.current = null;
    }
  }, []);

  const showReplacementMessage = useCallback(
    (replacement: string): void => {
      setReplacementMessage(replacement);
      clearReplacementTimeout();
      replacementTimeoutRef.current = setTimeout(() => {
        setReplacementMessage(null);
        replacementTimeoutRef.current = null;
      }, 3000);
    },
    [clearReplacementTimeout]
  );

  const handleTest = (): void => {
    setErrorMessage(null);
    flowchartLogger.debug('Testing match replace', testInput, 'against', matchRules);
    void axiosInstance
      .post('/api/v1/match_replace_test', {
        input: [testInput],
        match_rules: matchRules,
      })
      .catch((error) => {
        flowchartLogger.error('Error while testing match replace', error);
        if (axios.isAxiosError(error) && error.response?.status === 402) {
          notify('ord.httpErrors.402', { type: 'error' });
          return;
        }
        const validationMessage = getValidationMessage(error);
        if (validationMessage !== null) {
          setErrorMessage(validationMessage);
        }
        // Redirect to login if not authenticated
        authProvider?.checkError(error).catch(() => {
          redirectLogin();
        });
      })
      .then((response) => {
        flowchartLogger.debug('Match replace test response', response);
        if (response === undefined || response === null) return;
        const data = response.data as AxiosResponse<MatchReplaceTestResponse>['data'];
        if (data !== undefined && data.length > 0 && data[0] !== undefined) {
          const { matched_rule_index: matchedIndex, replacement } = data[0];
          if (matchedIndex !== undefined) {
            onTestResult(matchedIndex);
          }
          if (typeof replacement === 'string') {
            showReplacementMessage(replacement);
          } else {
            clearReplacementTimeout();
            setReplacementMessage(null);
          }
        }
      });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    flowchartLogger.debug('Match replace key down action', event.key);
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleTest();
    }
  };

  useEffect(() => {
    return () => {
      clearReplacementTimeout();
    };
  }, [clearReplacementTimeout]);

  return (
    <Box display='flex' flexDirection='column' gap={0.5} data-testid='match-replace-test-input'>
      <Box display='flex' alignItems='stretch' gap={1}>
        <TextField
          label={label}
          value={testInput}
          onChange={(event) => {
            setTestInput(event.target.value);
            setErrorMessage(null);
          }}
          onKeyDown={handleKeyDown}
          sx={{ '& .MuiInputBase-root': { height: '100%' } }}
        />
        <Button
          variant='outlined'
          color='info'
          size='large'
          type='button'
          onClick={(event) => {
            event.preventDefault();
            handleTest();
          }}
        >
          {translate('callflows.components.matchReplaceTestInput.testSubmit')}
        </Button>
      </Box>
      {replacementMessage !== null && (
        <Typography variant='caption' color='text.secondary'>
          {translate('callflows.components.matchReplaceTestInput.replacedWith', {
            replacement: replacementMessage,
            _: `Replaced with ${replacementMessage}`,
          })}
        </Typography>
      )}
      {errorMessage !== null && (
        <Typography variant='caption' color='error.main'>
          {translate('callflows.components.matchReplaceTestInput.testError', {
            error: errorMessage,
            _: `Validation error: ${errorMessage}`,
          })}
        </Typography>
      )}
    </Box>
  );
};

export default MatchReplaceTestInput;
